import {
    BarChartOutlined,
    CalculatorOutlined,
    ContactsOutlined,
    CreditCardOutlined,
    EditOutlined,
    ExportOutlined,
    FileTextOutlined,
    FormOutlined,
    InboxOutlined,
    ProfileOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import React, { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../../../layouts/MainLayout';
import { findFirstAvailableMenuItem } from 'utils/menuUtils';
import { hasResource } from 'utils/resourceAuth';
import { ResourceCode } from 'constants/resourceCodes';

const SettlementLayout: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const menuItems = useMemo(
        () => [
            {
                key: 'finance-statistics',
                label: t('settlement.financeStatistics.title'),
                icon: <BarChartOutlined />,
                display: hasResource([ResourceCode.menu.SETTLEMENT_FINANCE_STATISTICS]),
                children: [
                    {
                        key: 'realtime-income-statistics',
                        label: t('settlement.financeStatistics.realtimeIncomeStatistics'),
                        display: hasResource([ResourceCode.menu.SETTLEMENT_REVENUE_REALTIME_STATISTICS]),
                        onClick: () => navigate('/settlement/realtime-income-statistics'),
                    },
                    {
                        key: 'billing-period-income-statistics',
                        label: t('settlement.financeStatistics.billingPeriodIncomeStatistics'),
                        display: hasResource([ResourceCode.menu.SETTLEMENT_REVENUE_BILLING_PERIOD_STATISTICS]),
                        onClick: () => navigate('/settlement/billing-period-income-statistics'),
                    },
                    {
                        key: 'dsp-expense-statistics',
                        label: t('settlement.financeStatistics.dspExpenseStatistics'),
                        display: hasResource([ResourceCode.menu.SETTLEMENT_DSP_EXPENSE_STATISTICS]),
                        onClick: () => navigate('/settlement/dsp-expense-statistics'),
                    },
                ],
            },
            {
                key: 'settlement-center',
                label: t('settlement.settlementCenter.title'),
                icon: <CreditCardOutlined />,
                display: hasResource([ResourceCode.menu.SETTLEMENT_CENTER]),
                children: [
                    {
                        key: 'clearing-voucher',
                        label: t('settlement.clearingVoucher.title'),
                        icon: <ProfileOutlined />,
                        display: hasResource([ResourceCode.menu.SETTLEMENT_CLEARING_VOUCHER]),
                        onClick: () => navigate('/settlement/clearing-voucher'),
                    },
                    {
                        key: 'account/account-management',
                        label: t('settlement.accountManagement.title'),
                        icon: <ContactsOutlined />,
                        display: hasResource([ResourceCode.menu.SETTLEMENT_ACCOUNT_MANAGEMENT]),
                        onClick: () => navigate('/settlement/account/account-management'),
                    },
                    {
                        key: 'bill-list',
                        label: t('settlement.settlementBill.title'),
                        icon: <FileTextOutlined />,
                        display: hasResource([ResourceCode.menu.SETTLEMENT_BILL]),
                        onClick: () => navigate('/settlement/bill-list'),
                    },
                    {
                        key: 'pricing-query',
                        label: t('settlement.pricingQuery.title'),
                        icon: <SearchOutlined />,
                        display: hasResource([ResourceCode.menu.SETTLEMENT_PRICING_QUERY]),
                        onClick: () => navigate('/settlement/pricing-query'),
                    },
                    {
                        key: 'fee-item-management',
                        label: t('settlement.feeItemManager.title'),
                        icon: <ProfileOutlined />,
                        display: hasResource([ResourceCode.menu.SETTLEMENT_FEE_ITEM_MANAGER]),
                        onClick: () => navigate('/settlement/fee-item-management'),
                    },
                    {
                        key: 'bill-export-template-config',
                        label: t('settlement.billExportTemplateConfig.title'),
                        icon: <ExportOutlined />,
                        display: hasResource([
                            ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_CHECK,
                            ResourceCode.menu.OPS_TOOL_SETTLEMENT,
                        ]),
                        onClick: () => navigate('/settlement/bill-export-template-config'),
                    },
                ],
            },
            {
                key: 'price-center',
                label: t('settlement.priceCenter.title'),
                icon: <CalculatorOutlined />,
                display: hasResource([ResourceCode.menu.SETTLEMENT_PRICE_CENTER]),
                onClick: () => navigate('/settlement/price-center'),
            },
            {
                key: 'settlement-review',
                label: t('settlement.settlementReview.title'),
                icon: <ProfileOutlined/>,
                display: hasResource([ResourceCode.menu.SETTLEMENT_REVIEW]),
                children: [
                    {
                        key: 'adjustment-review',
                        label: t('settlement.adjustmentReview.title'),
                        icon: <ProfileOutlined/>,
                        display: hasResource([ResourceCode.menu.SETTLEMENT_ADJUSTMENT_REVIEW]),
                        onClick: () => navigate('/settlement/adjustment-review'),
                    },
                ],
            },
            {
                key: 'revise',
                label: t('settlement.revise.title'),
                icon: <EditOutlined />,
                display: hasResource([ResourceCode.menu.SETTLEMENT_REVISE]),
                children: [
                    {
                        key: 'revise/pricing-failure-export',
                        label: t('settlement.revise.pricingFailureExport'),
                        icon: <ExportOutlined />,
                        display: hasResource([ResourceCode.menu.SETTLEMENT_PRICING_FAILURE_EXPORT]),
                        onClick: () => navigate('/settlement/revise/pricing-failure-export')
                    },
                    {
                        key: 'revise/pre-bill-revision',
                        label: t('settlement.revise.preBillRevision'),
                        icon: <FormOutlined />,
                        display: hasResource([ResourceCode.menu.SETTLEMENT_PRE_BILL_REVISION]),
                        onClick: () => navigate('/settlement/revise/pre-bill-revision')
                    }
                ]
            },
            {
                key: 'import-data',
                label: '结算数据导入',
                icon: <InboxOutlined />,
                display: hasResource([ResourceCode.menu.SETTLEMENT_PRE_BILL_REVISION]),
                onClick: () => navigate('/settlement/import-data'),
            },
        ],
        [navigate, t]
    );

    useEffect(() => {
        const isRoot = location.pathname === '/settlement' || location.pathname === '/settlement/';
        if (!isRoot) return;
        const first = findFirstAvailableMenuItem(menuItems);
        if (first?.onClick) first.onClick();
    }, [location.pathname, menuItems]);

    return (
        <MainLayout
            activeFirstMenu="settlement"
            secondMenu={menuItems}
        >
            <div className="p-6 h-full flex flex-col">
                <Outlet />
            </div>
        </MainLayout>
    );
};

export default SettlementLayout;
